import { logger } from '@beelday/common'
import { useEffect, useRef, useState } from 'react'
import { ClickerCommand, clickerCommand } from './clicker'
import { useClickerPresses } from './clicker-presses'

/**
 * How long a press waits for the server to act on it before the clicker takes
 * presses again. Normally the result arrives well before; this only frees a
 * clicker whose press the server ignored.
 */
export const CLICKER_SETTLE_MS = 4000

/** How long a screen keeps the pressed command lit. */
export const CLICKER_FLASH_MS = 600

type Options = {
	enabled: boolean
	/** Changes whenever the last press has taken effect. */
	stepKey: string
	/**
	 * What a press does on this screen: the request it sent, or `undefined`
	 * when the command means nothing here.
	 */
	act: (command: ClickerCommand) => Promise<unknown> | undefined
}

/** Nothing on the page has the focus: the arrow keys are the clicker's. */
export const isPageFocused = (): boolean => {
	const focused = document.activeElement
	return !focused || focused === document.body
}

/**
 * The trainer's presentation clicker (see `clicker.ts`), pressed here or in
 * another window (`clicker-presses.tsx`). Returns the command just pressed,
 * for the screen to light it.
 *
 * One press at a time: clickers bounce and double-send, and the server takes
 * one step per request, so a press made before the last one took effect would
 * skip a step. Such presses are dropped.
 */
export const useClicker = ({
	enabled,
	stepKey,
	act,
}: Options): ClickerCommand | undefined => {
	const [pressed, setPressed] = useState<ClickerCommand>()
	const presses = useClickerPresses()
	const waiting = useRef(false)
	const latestAct = useRef(act)
	latestAct.current = act

	// The press took effect: the clicker is free again.
	useEffect(() => {
		waiting.current = false
	}, [stepKey])

	useEffect(() => {
		if (!enabled) {
			return
		}
		let release: number | undefined
		let unlight: number | undefined

		const onKeyDown = (event: KeyboardEvent) => {
			const command = clickerCommand(event, { pageFocused: isPageFocused() })
			if (!command) {
				return
			}
			// Page Down would scroll the projector's page.
			event.preventDefault()
			onPress(command)
		}

		const onPress = (command: ClickerCommand): void => {
			if (waiting.current) {
				return
			}
			const request = latestAct.current(command)
			if (!request) {
				return
			}
			waiting.current = true
			window.clearTimeout(release)
			release = window.setTimeout(() => {
				waiting.current = false
			}, CLICKER_SETTLE_MS)

			setPressed(command)
			window.clearTimeout(unlight)
			unlight = window.setTimeout(() => setPressed(undefined), CLICKER_FLASH_MS)

			request.catch(error => {
				logger.error('The clicker press failed', error)
				waiting.current = false
			})
		}

		window.addEventListener('keydown', onKeyDown)
		const unsubscribe = presses?.subscribe(onPress)
		return () => {
			window.removeEventListener('keydown', onKeyDown)
			unsubscribe?.()
			window.clearTimeout(release)
			window.clearTimeout(unlight)
		}
	}, [enabled, presses])

	return pressed
}

export default useClicker
